/**
 * Billing Commands
 *
 * Commands dispatched via the YJS pendingCmds mechanism to keep billing data
 * up-to-date across all open tabs in real-time.
 *
 * billing_refresh — triggered by the backend after every AI billing write and by
 * chatStore.onFinally after every stream. Forces a cache-bypassing refetch of
 * the current bill on every connected tab.
 *
 * payment_failed_notification — triggered by the backend when invoice.payment_failed
 * fires. Refreshes billing data and shows a persistent in-app banner so users
 * immediately know their AI usage has been paused.
 */

import { SelfContainedCommand } from '../../types/commands';
import { StandardToolResult, createSuccessResult } from '../../types/standardToolResults';

const billingRefreshCommand: SelfContainedCommand = {
  id: 'billing_refresh',
  description: 'Force-refresh billing data on this tab (triggered via YJS pendingCmds)',
  autonomyLevel: 'auto',

  schema: { parameters: [] },

  handler: async (): Promise<StandardToolResult> => {
    try {
      const { default: useUserStore } = await import('../../stores/userStore');
      await useUserStore.getState().fetchCurrentBill({ force: true });
    } catch (err) {
      console.warn('[BillingCmd] billing_refresh failed (non-fatal):', err);
    }
    return createSuccessResult('Billing refreshed');
  },
} as SelfContainedCommand;

const paymentFailedNotificationCommand: SelfContainedCommand = {
  id: 'payment_failed_notification',
  description: 'Show payment-failed banner and refresh billing data when a payment fails',
  autonomyLevel: 'auto',

  schema: { parameters: [] },

  handler: async (): Promise<StandardToolResult> => {
    try {
      const { default: useUserStore } = await import('../../stores/userStore');
      await useUserStore.getState().fetchCurrentBill({ force: true });
    } catch (err) {
      console.warn('[BillingCmd] payment_failed_notification: billing refresh failed (non-fatal):', err);
    }

    try {
      const { toastStore } = await import('../../stores/toastStore');
      const settingsUrl = '/settings';
      toastStore.add({
        id: 'payment_failed_billing',
        message: 'Payment failed. AI usage paused. Update your payment method to resume.',
        priority: 'urgent',
        duration: 0,
        actionUrl: settingsUrl,
      });
    } catch (err) {
      console.warn('[BillingCmd] payment_failed_notification: toast failed (non-fatal):', err);
    }

    return createSuccessResult('Payment failed notification shown');
  },
} as SelfContainedCommand;

export const billingCommands: SelfContainedCommand[] = [
  billingRefreshCommand,
  paymentFailedNotificationCommand,
];
